import React from 'react';
import { createSelector } from 'reselect';
import { fromJS } from 'immutable';

import { parseIdentityProvider } from 'utils/identityProviders';
import { convertSecondsToReadableDate, opMoment } from 'utils/dates';
import { selectCurrentOrgId } from 'global/accessToken/selectors';
import { selectFeatureFlag } from 'global/openpathconfig/selectors';
import { BILLING_SUBSCRIPTION_FORM } from 'routes/MasterOrgsEditPackagePage/constants';
import { selectIdentityEmail } from 'routes/AppContainer/selectors';
import { VIDEO_READER_ACU_MODEL_IDS } from 'utils/constants';

import {
  ENTRIES_REDUCER,
  ACUS_REDUCER,
  ACU_PORTS_REDUCER,
  CREDENTIAL_TYPES_REDUCER,
  USERS_REDUCER,
  LOCKDOWN_PLANS_REDUCER,
  SITES_REDUCER,
  ZONES_REDUCER,
  ACTIVITY_REDUCER,
  OPVIDEO_DEVICE_FRAGMENTS_REDUCER,
  VIDEO_PROVIDERS_REDUCER,
  WIRELESS_LOCK_PROVIDERS_REDUCER,
  ALLEGION_OPERATORS_REDUCER,
  ACU_CONFIG_REDUCER,
  ALARM_CONFIGURATIONS_REDUCER,
  READERS_REDUCER,
  ROLES_REDUCER,
  ALARM_ACTIVITY_REDUCER,
  OPVIDEO_DEVICES_REDUCER,
} from './constants';
import TooltipContent from './components/TooltipContent';

const selectPage = () => (state, props) =>
  state.get(props.route.name, fromJS({}));

export const selectPageData = () =>
  createSelector(selectPage(), (page) => page.get('pageData'));

// The following two selectors are setup to be used multiple times per page
// If you need to use instance functionality on a different reducer here,
// you'll have to modify the action to take an 'instance' prop and then
// modify the selectors to use it.
export const selectGroups = (instance) =>
  createSelector(selectPage(), (page) =>
    page.getIn([instance || 'groups'], fromJS([])),
  );

export const selectUserGroups = (instance) =>
  createSelector(selectPage(), (page) =>
    page.getIn([instance || 'userGroups'], fromJS([])),
  );

export const selectUsers = (instance) =>
  createSelector(selectPage(), (page) => page.get(instance || 'users'));

export const selectfilteredUsersCount = (instance) =>
  createSelector(selectPage(), (page) =>
    page.get(instance || 'filteredUsersCount'),
  );

export const selectTotalUsersCount = (instance) =>
  createSelector(selectPage(), (page) =>
    page.get(instance || 'totalUsersCount'),
  );

export const selectAllAcuEntriesUsers = () =>
  createSelector(selectPage(), (page) => page.get('allAcuEntriesUsers'));

export const selectOrg = () =>
  createSelector(selectPage(), (page) => page.get('org'));

export const selectBillableFeatures = () =>
  createSelector(selectOrg(), (org) => org.get('billableFeatures', fromJS([])));

export const selectIdentityProviders = () =>
  createSelector(selectPage(), (page) => page.get('identityProviders'));

export const selectPackagePlans = () =>
  createSelector(selectPage(), (page) => page.get('netsuitePackagePlans'));

export const selectFilteredPackagePlans = () =>
  createSelector(selectPage(), (page) => {
    // We only want to display base packages for which there are currently available package plans for the selected interval, term length, and currency
    const formValues = page.getIn([
      'form',
      BILLING_SUBSCRIPTION_FORM,
      'values',
    ]);
    if (formValues && formValues.get('interval')) {
      return page
        .get('netsuitePackagePlans')
        .filter(
          (pp) =>
            pp.getIn(['netsuitePlan', 'interval']) ===
              formValues.get('interval') &&
            pp.getIn(['netsuitePlan', 'interval_count']) ===
              formValues.get('interval_count') &&
            pp.getIn(['netsuitePlan', 'currency']) ===
              formValues.get('currency'),
        );
    }
    return page.get('netsuitePackagePlans');
  });

export const selectCurrentParentPackagePlan = () =>
  createSelector(
    selectPackagePlans(),
    (packagePlans) =>
      packagePlans.find(
        (plan) =>
          plan.get('isSelected') === true &&
          !plan.getIn(['package', 'parentPackages']).size,
      ) || fromJS({}),
  );

export const selectCurrentNetsuiteParentPackagePlan = () =>
  createSelector(
    selectFilteredPackagePlans(),
    (packagePlans) =>
      packagePlans.find(
        (plan) =>
          plan.get('isSelected') === true &&
          !plan.getIn(['package', 'parentPackages']).size,
      ) || fromJS({}),
  );

export const selectPackageEnabledById = () =>
  createSelector(selectPackagePlans(), (packagePlans) =>
    packagePlans
      .filter((plan) => plan.get('isSelected'))
      .reduce(
        (acc, plan) => acc.set(String(plan.getIn(['package', 'id'])), true),
        fromJS({}),
      ),
  );

export const selectPackages = () =>
  createSelector(selectPackagePlans(), (data) =>
    fromJS(
      data.reduce((acc, cur) => {
        if (!cur.get('netsuitePlan')) return acc;
        const foundPackageIndex = acc.findIndex(
          (pkg) => pkg.get('id') === cur.getIn(['package', 'id']),
        );
        if (foundPackageIndex === -1) {
          const pkg = cur
            .get('package')
            .set(
              'netsuitePlans',
              fromJS([
                cur
                  .get('netsuitePlan')
                  .set('isSelected', cur.get('isSelected')),
              ]),
            );
          return acc.push(pkg);
        }
        return acc.updateIn([foundPackageIndex, 'netsuitePlans'], (arr) =>
          arr.push(
            cur.get('netsuitePlan').set('isSelected', cur.get('isSelected')),
          ),
        );
      }, fromJS([])),
    ),
  );

export const selectNetsuitePackages = () =>
  createSelector(selectFilteredPackagePlans(), (data) =>
    fromJS(
      data.reduce((acc, cur) => {
        if (!cur.get('netsuitePlan')) return acc;
        const foundPackageIndex = acc.findIndex(
          (pkg) => pkg.get('id') === cur.getIn(['package', 'id']),
        );
        if (foundPackageIndex === -1) {
          const pkg = cur
            .get('package')
            .set(
              'netsuitePlans',
              fromJS([
                cur
                  .get('netsuitePlan')
                  .set('isSelected', cur.get('isSelected')),
              ]),
            );
          return acc.push(pkg);
        }
        return acc.updateIn([foundPackageIndex, 'netsuitePlans'], (arr) =>
          arr.push(
            cur.get('netsuitePlan').set('isSelected', cur.get('isSelected')),
          ),
        );
      }, fromJS([])),
    ),
  );

export const selectNetsuitePlanIntervals = () =>
  createSelector(selectPackages(), (packages) =>
    fromJS(
      packages.reduce((acc, currentPackage) => {
        currentPackage.get('netsuitePlans').forEach((netsuitePlan) => {
          if (!acc.includes(netsuitePlan.get('interval'))) {
            acc.push(netsuitePlan.get('interval'));
          }
        });
        return acc;
      }, []),
    ),
  );

export const selectNetsuitePlanLengths = () =>
  createSelector(selectPackages(), (packages) =>
    fromJS(
      packages.reduce((acc, currentPackage) => {
        currentPackage.get('netsuitePlans').forEach((netsuitePlan) => {
          if (
            acc.findIndex(
              (a) => a.value === netsuitePlan.get('interval_count'),
            ) === -1
          ) {
            const length = netsuitePlan.get('interval_count');
            acc.push({
              label: `${length} year${length > 1 ? 's' : ''}`,
              value: length,
            });
          }
        });
        return acc;
      }, []),
    ),
  );

export const selectOptionsForCurrenciesMultiSelect = () =>
  createSelector(selectPackages(), (packages) =>
    fromJS(
      packages.reduce((acc, currentPackage) => {
        currentPackage.get('netsuitePlans').forEach((netsuitePlan) => {
          const currency = netsuitePlan.get('currency');

          // Only add currency to array if it doesn't already exist
          if (
            !acc
              .map(({ value }) => value)
              .includes(netsuitePlan.get('currency'))
          ) {
            acc.push({ label: currency.toUpperCase(), value: currency });
          }
        });
        return acc;
      }, []),
    ),
  );

export const selectEntries = () =>
  createSelector(selectPage(), (page) => page.get('entries', fromJS([])));

export const selectOptionsForEntriesMultiSelect = () =>
  createSelector(selectEntries(), (entries) =>
    entries.map((entry) => ({
      label: entry.get('name'),
      value: entry.get('id'),
    })),
  );

export const selectGroup = () =>
  createSelector(selectPage(), (page) => page.get('group'));

export const selectGroupUserCount = () =>
  createSelector(selectGroup(), (group) => group.get('userCount') || 0);

export const selectGroupsForMultiSelect = () =>
  createSelector(selectGroups(), (orgGroups) =>
    orgGroups.map((group) => ({
      label: group.get('name'),
      value: group.get('id'),
    })),
  );

export const selectSites = () =>
  createSelector(selectPage(), (page) => page.get('sites') || fromJS([]));

export const selectSitesForMultiSelect = () =>
  createSelector(selectSites(), (orgSites) =>
    orgSites.map((site) => ({
      label: site.get('name'),
      value: site.get('id'),
    })),
  );

export const selectZones = () =>
  createSelector(selectPage(), (page) => page.get('zones'));

export const selectAlarmConfigurations = () =>
  createSelector(selectPage(), (page) => page.get('alarmConfigurations'));

export const selectZone = () =>
  createSelector(selectPage(), (page) => page.get('zone'));

export const selectZoneEntries = () =>
  createSelector(selectZone(), (zone) => zone.get('entries'));

export const selectOptionsForZoneEntriesMultiSelect = () =>
  createSelector(selectZone(), (zone) => {
    const zoneEntries = zone.get('entries');
    return zoneEntries
      ? zoneEntries.map((zoneEntry) => ({
          label: zoneEntry.get('name'),
          value: zoneEntry.get('id'),
        }))
      : [];
  });

export const selectZoneGroupCount = () =>
  createSelector(selectZone(), (zone) => zone.get('groupCount') || 0);

export const selectZoneUserCount = () =>
  createSelector(selectZone(), (zone) => zone.get('userCount') || 0);

export const selectSiteZoneMap = () =>
  createSelector(selectZones(), (orgZones) =>
    fromJS(
      orgZones.reduce((acc, currentZone) => {
        const zoneObj = {
          id: currentZone.get('id'),
          name: currentZone.get('name'),
          entryCount: currentZone.get('entryCount'),
          offlineEntryCount: currentZone.get('offlineEntryCount'),
          entries: currentZone.get('entries'),
        };
        if (!acc[currentZone.getIn(['site', 'id'])]) {
          // if site doesn't exist as key yet, create key using site's id
          // eslint-disable-next-line no-param-reassign
          acc[currentZone.getIn(['site', 'id'])] = {
            name: currentZone.getIn(['site', 'name']), // site name
            zones: [zoneObj],
          };
        } else {
          // site key already exists so push zone info into array
          acc[currentZone.getIn(['site', 'id'])].zones.push(zoneObj);
        }

        return acc;
      }, {}),
    ),
  );

export const selectZonesForMultiSelect = () =>
  createSelector(selectZones(), (orgZones) =>
    orgZones.map((zone) => ({
      label: zone.get('name'),
      value: zone.get('id'),
    })),
  );

export const selectOwnedZones = () =>
  createSelector([selectCurrentOrgId(), selectPage()], (orgId, page) =>
    page.get('zones').filter((zone) => zone.getIn(['org', 'id']) === orgId),
  );

export const selectOwnedZonesForMultiSelect = () =>
  createSelector(selectOwnedZones(), (orgZones) =>
    orgZones.map((zone) => ({
      label: zone.get('name'),
      value: zone.get('id'),
    })),
  );

export const selectOptionsForGroupsMultiSelect = () =>
  createSelector(selectGroups(), (Groups) =>
    Groups.map((user) => {
      const value = user.get('id');
      const label = user.getIn(['name']);

      return {
        label,
        value,
      };
    }),
  );

export const selectUserEntries = () =>
  createSelector(selectPage(), (page) => page.get('userEntries', fromJS([])));

export const selectUserCredentials = () =>
  createSelector(selectPage(), (page) =>
    page.get('userCredentials', fromJS([])),
  );

export const selectOptionsForUserEntriesMultiSelect = () =>
  createSelector(selectUserEntries(), (userEntries) =>
    userEntries.map((entry) => ({
      label: entry.get('name'),
      value: entry.get('id'),
    })),
  );

export const selectEntryStates = () =>
  createSelector(selectPage(), (page) => page.get('entryStates'));

export const selectEntryStatesForMultiSelect = () =>
  createSelector(selectEntryStates(), (orgEntryStates) =>
    orgEntryStates.map((entryState) => ({
      label: entryState.get('name'),
      value: entryState.get('id'),
    })),
  );

export const selectTriggerMethods = () =>
  createSelector(
    selectPage(),
    selectFeatureFlag('ENABLED_TRIGGER_METHOD_CODES'),
    (page, enabledCodes) => {
      let result = page.get('triggerMethods');
      if (enabledCodes) {
        result = result.filter((m) => enabledCodes.includes(m.get('code')));
      }
      return result;
    },
  );

export const selectIos = () =>
  createSelector(selectPage(), (page) => page.get('ios'));

export const selectAcus = () =>
  createSelector(selectPage(), (page) => page.get('acus'));

export const selectAcusForMultiSelect = () =>
  createSelector(selectAcus(), (orgAcus) =>
    orgAcus.map((acu) => ({
      label: acu.get('name'),
      value: acu.get('id'),
      acuModelId: acu.getIn(['acuModel', 'id']),
    })),
  );

export const selectWirelessLocks = () =>
  createSelector(selectPage(), (page) => page.get('wirelessLocks'));

export const selectWirelessLockProviderIdForAllegion = () =>
  createSelector(
    selectOrgContainerReducer(WIRELESS_LOCK_PROVIDERS_REDUCER)(),
    (wirelessLockProviders) => {
      if (!wirelessLockProviders.get('hasLoaded')) {
        return null;
      }

      return wirelessLockProviders
        ? wirelessLockProviders
            .get('data')
            .find(
              (providerObject) =>
                providerObject.getIn(['wirelessLockProviderType', 'id']) === 1, // Allegion
            )
            ?.get?.('id')
        : null;
    },
  );

export const selectIsUserAnAllegionOperator = () =>
  createSelector(
    selectOrgContainerReducer(ALLEGION_OPERATORS_REDUCER)(),
    selectIdentityEmail(),
    (allegionOperatorsReducer, identityEmail) => {
      if (!allegionOperatorsReducer.get('hasLoaded') || !identityEmail) {
        return undefined;
      }

      return !!allegionOperatorsReducer
        .get('data')
        ?.find?.((operator) => operator?.get?.('userName') === identityEmail);
    },
  );

export const selectWirelessLocksForMultiSelect = () =>
  createSelector(selectWirelessLocks(), (wirelessLocks) =>
    wirelessLocks.map((wirelessLock) => ({
      id: wirelessLock.get('id'),
      entryId: wirelessLock.getIn(['entry', 'id']),
      label: wirelessLock.get('name'),
      value: wirelessLock.get('id'),
    })),
  );

export const selectScopesFlat = () =>
  createSelector(
    selectPage(),
    (page) =>
      page
        .get('scopeResources')
        .reduce((acc, sr) => acc.concat(sr.get('scopes')), fromJS([])),
    // const reducer = (accumulator, currentValue) => accumulator + currentValue;
  );

export const selectScopeResources = () =>
  createSelector(selectPage(), (page) => {
    // the magic to set up the childScopes field
    const processedCodes = [];
    const scopeResources = page.get('scopeResources');
    return (
      scopeResources
        // .filter(sr => sr.get('name') === 'Users')
        .map((sr) => {
          // loop through scopes and make sure they have their grouping code
          const newScopes = sr
            .get('scopes')
            .map((s) => {
              // console.debug(`[s] - ${JSON.stringify(s, null, 2)}`)
              const code = s.get('code');
              const c = code.match(/((o){(\d*\w*)}-)?(.*?):(\w)/);
              const [, , , , groupingCode] = c;
              return s.set('groupingCode', groupingCode);
            })

            .map((s, i, og) => {
              if (processedCodes.includes(s.get('groupingCode'))) {
                return undefined;
              }
              processedCodes.push(s.get('groupingCode'));
              return fromJS({
                groupingCode: s.get('groupingCode'),
                ordinal: s.get('ordinal'),
                scopes: og.filter(
                  (x) => x.get('groupingCode') === s.get('groupingCode'),
                ),
              });
            })
            .filter((x) => x); // this is because we return undefined above, could refactor to reduce
          return sr.set('scopes', newScopes);
        })
    );
  });

export const selectRoles = () =>
  createSelector(selectPage(), (page) => page.get('roles'));

export const selectRole = () =>
  createSelector(selectPage(), (page) => page.get('role'));

export const selectRoleEditable = () =>
  createSelector(selectRole(), (role) => role.get('isEditable') || false);

export const selectRoleUserCount = () =>
  createSelector(selectRole(), (role) => role.get('userCount') || 0);

export const selectGroupSchedules = () =>
  createSelector(selectPage(), (page) => page.get('groupSchedules'));

export const selectOptionsForGroupSchedulesMultiSelect = () =>
  createSelector(selectGroupSchedules(), (groupSchedules) =>
    groupSchedules
      .filter((schedule) => schedule.getIn(['scheduleType', 'id']) === 3)
      .map((schedule) => ({
        label: schedule.get('name'),
        value: schedule.get('id'),
      })),
  );

export const selectActivity = () =>
  createSelector(selectPage(), (page) => page.get('activity'));
export const selectVisualActivity = () =>
  createSelector(selectPage(), (page) => page.get('visualActivity'));

export const selectTableFormattedActivityLogs = () =>
  createSelector(selectPage(), selectPageData(), (page, pageData) => {
    let activity = page.get('activity');
    let filterOptions = pageData.get('filterOptions');
    const sortOptions = pageData.get('sortOptions');
    let sortColumn = null;
    let sortOrderIsAscending = null;

    /* Assuming hard coded columns to increase performance (sort->map instead
    of map->sort->map due to converting time to readable date). The keys match
    the ids of the columns, and the values match the keys of the helium data. */
    const columnIdToHeliumKey = {
      time: 'time',
      userName: 'userName',
      userOrgName: 'userOrgName',
      credentialTypeName: 'credentialTypeName',
      credentialDetail: 'credentialDetail',
      acuName: 'acuName',
      entryName: 'entryName',
      targetType: 'targetType',
      targetName: 'targetName',
      requestType: 'requestType',
      location: 'location',
      result: 'result',
      deniedReason: 'deniedReason',
    };

    // Remove any filters with empty strings from the list
    filterOptions = filterOptions.filter((value) => value !== '');

    // Filter activity
    if (filterOptions.size) {
      activity = activity.filter((value) => {
        let shouldKeep = true;

        // Check if the column cell contains the corresponding columnFilter
        filterOptions.forEach((columnFilter, columnId) => {
          // Check if the cell contains the columnFilter
          const filter = new RegExp(columnFilter, 'gi');
          shouldKeep =
            shouldKeep && filter.test(value.get(columnIdToHeliumKey[columnId]));
        });

        return shouldKeep;
      });
    }

    // Sort activity
    if (sortOptions && sortOptions.size) {
      sortOptions.entrySeq().forEach(([key, value]) => {
        if (value.get('isSelected')) {
          sortColumn = key;
          sortOrderIsAscending = value.get('sortOrderIsAscending');
        }
      });
    }

    if (sortColumn) {
      activity = sortOrderIsAscending
        ? activity.sortBy((a) => a.get(columnIdToHeliumKey[sortColumn]))
        : activity
            .sortBy((a) => a.get(columnIdToHeliumKey[sortColumn]))
            .reverse();
    }

    const formattedActivity = activity.map((log) => ({
      time: convertSecondsToReadableDate(log.get('time')),
      userName: log.get('userName'),
      userOrgName: log.get('userOrgName'),
      credentialTypeName: log.get('credentialTypeName'),
      credentialDetail: log.get('credentialDetail'),
      acuName: log.get('acuName'),
      entryName: log.get('entryName'),
      targetType: log.get('targetType'),
      targetName: log.get('targetName'),
      requestType: log.get('requestType'),
      location: log.get('location'),
      result: log.get('result'),
      deniedReason: log.get('deniedReason') ? log.get('deniedReason') : '--',
    }));

    return fromJS(formattedActivity);
  });

export const selectUserActivity = () =>
  createSelector(selectPage(), (page) => page.get('userActivity'));

export const selectTableFormattedUserActivityLogs = () =>
  createSelector(selectPage(), selectPageData(), (page, pageData) => {
    let activity = page.get('userActivity');
    let filterOptions = pageData.get('filterOptions');
    const sortOptions = pageData.get('sortOptions');
    let sortColumn = null;
    let sortOrderIsAscending = null;

    /* Assuming hard coded columns to increase performance (sort->map instead
    of map->sort->map due to converting time to readable date). The keys match
    the ids of the columns, and the values match the keys of the helium data. */
    const columnIdToHeliumKey = {
      time: 'time',
      userName: 'userName',
      userOrgName: 'userOrgName',
      credentialTypeName: 'credentialTypeName',
      credentialDetail: 'credentialDetail',
      acuName: 'acuName',
      entryName: 'entryName',
      targetType: 'targetType',
      targetName: 'targetName',
      requestType: 'requestType',
      location: 'location',
      result: 'result',
      deniedReason: 'deniedReason',
    };

    // Remove any filters with empty strings from the list
    filterOptions = filterOptions.filter((value) => value !== '');

    // Filter activity
    if (filterOptions.size) {
      activity = activity.filter((value) => {
        let shouldKeep = true;

        // Check if the column cell contains the corresponding columnFilter
        filterOptions.forEach((columnFilter, columnId) => {
          // Check if the cell contains the columnFilter
          const filter = new RegExp(columnFilter, 'gi');
          shouldKeep =
            shouldKeep && filter.test(value.get(columnIdToHeliumKey[columnId]));
        });

        return shouldKeep;
      });
    }

    // Sort activity
    if (sortOptions && sortOptions.size) {
      sortOptions.entrySeq().forEach(([key, value]) => {
        if (value.get('isSelected')) {
          sortColumn = key;
          sortOrderIsAscending = value.get('sortOrderIsAscending');
        }
      });
    }

    if (sortColumn) {
      activity = sortOrderIsAscending
        ? activity.sortBy((a) => a.get(columnIdToHeliumKey[sortColumn]))
        : activity
            .sortBy((a) => a.get(columnIdToHeliumKey[sortColumn]))
            .reverse();
    }

    const formattedUserActivity = activity.map((log) => ({
      time: convertSecondsToReadableDate(log.get('time')),
      userName: log.get('userName'),
      userOrgName: log.get('userOrgName'),
      credentialTypeName: log.get('credentialTypeName'),
      credentialDetail: log.get('credentialDetail'),
      acuName: log.get('acuName'),
      entryName: log.get('entryName'),
      targetType: log.get('targetType'),
      targetName: log.get('targetName'),
      requestType: log.get('requestType'),
      location: log.get('location'),
      result: log.get('result'),
      deniedReason: log.get('deniedReason') ? log.get('deniedReason') : '--',
    }));

    return fromJS(formattedUserActivity);
  });

export const selectIdentityProviderGroups = () =>
  createSelector(selectPage(), (page) =>
    page.get('identityProviderGroups', fromJS([])),
  );

export const selectIdentityProviderGroupRelations = () =>
  createSelector(selectPage(), (page) =>
    page.get('identityProviderGroupRelations'),
  );

export const selectEntryUserSchedules = () =>
  createSelector(selectPage(), (page) => page.get('entryUserSchedules'));

export const selectCredentialTypes = () =>
  createSelector(selectPage(), (page) => page.get('credentialTypes'));

export const selectCredentials = () =>
  createSelector(selectPage(), (page) => page.get('credentials'));

export const selectOptionsForCredentialTypesMultiSelect = () =>
  createSelector(
    selectCredentialTypes(),
    selectFeatureFlag('ENABLED_USER_CREDENTIAL_TYPES'),
    (orgCredentialTypes, enabledCredentialTypes) =>
      orgCredentialTypes
        .filter((credentialType) =>
          enabledCredentialTypes.includes(credentialType.get('modelName')),
        )
        .map((credentialType) => ({
          label: credentialType.get('name'),
          value: credentialType.get('id'),
        })),
  );

export const selectCardFormats = () =>
  createSelector(selectPage(), (page) => page.get('cardFormats'));

export const selectScheduleTypes = () =>
  createSelector(selectPage(), (page) => page.get('scheduleTypes'));

export const selectOptionsForScheduleTypesMultiSelect = () =>
  createSelector(selectScheduleTypes(), (scheduleTypes) =>
    scheduleTypes.map((scheduleType) => ({
      label: scheduleType.get('name'),
      value: scheduleType.get('id'),
    })),
  );

export const selectSchedules = () =>
  createSelector(selectPage(), (page) => page.get('schedules'));

export const selectOptionsForSchedulesMultiSelect = () =>
  createSelector(selectSchedules(), (schedules) =>
    schedules.map((schedule) => ({
      label: schedule.get('name'),
      value: schedule.get('id'),
    })),
  );

export const selectScheduleEvents = () =>
  createSelector(selectPage(), (page) => page.get('scheduleEvents'));

export const selectAuditLogs = () =>
  createSelector(selectPage(), (page) => page.get('auditLogs'));

export const selectSharedUsers = () =>
  createSelector(selectPage(), (page) => page.get('sharedUsers', fromJS([])));

export const selectAcuPorts = () =>
  createSelector(selectPage(), (page) => page.get('acuPorts'));

export const selectRequestTypes = () =>
  createSelector(selectPage(), (page) => page.get('requestTypes'));

export const selectProtonStatuses = () =>
  createSelector(selectPage(), (page) => page.get('protonStatuses'));

export const selectNamespaces = () =>
  createSelector(selectPage(), (page) => page.get('namespaces'));

export const selectHookEvents = () =>
  createSelector(selectPage(), (page) => page.get('hookEvents'));

export const selectHookAction = () =>
  createSelector(selectPage(), (page) => page.get('hookAction'));

/// ///////////////////

export const selectOrgContainerReducer = (reducerName, subFilter) => () =>
  createSelector(selectPage(), (page) => {
    return subFilter
      ? // Not using getIn as the combineReducer doesn't seem
        // to allow for making the key/value pair immutable (need
        // to look into more)
        page.get(subFilter)[reducerName]
      : page.get(reducerName);
  });

export const selectOrgContainerReducers = () => () =>
  createSelector(selectPage(), (page) => page);

export const selectData = (reducerName) => () =>
  createSelector(selectOrgContainerReducer(reducerName)(), (reducer) =>
    reducer.get('data'),
  );

export const selectFilteredCount = (reducerName) => () =>
  createSelector(selectOrgContainerReducer(reducerName)(), (reducer) =>
    reducer.get('filteredCount'),
  );

export const selectTotalCount = (reducerName) => () =>
  createSelector(selectOrgContainerReducer(reducerName)(), (reducer) =>
    reducer.get('totalCount'),
  );

// Allows retrieval of any item in a reducer
export const selectOrgContainerReducerItem =
  (reducerName, itemPath, subFilter, returnJs = false) =>
  () =>
    createSelector(
      selectOrgContainerReducer(reducerName, subFilter)(),
      (reducer) => {
        const x = itemPath ? reducer?.getIn(itemPath.split('.')) : reducer;
        if (x && returnJs) {
          return x.toJS();
        }
        return x;
      },
      // not something I want to do a lot, but this reducer crashes in certain injection scenarios (video widget)
      // due to injection of reducer not finishing before selector fires? This solves it
      // but we can revisit after unit tests are added -justin
    );

// Select options for a Sementic UI React <Select>
const defaultDataMapper = (data) =>
  data.map((row) => ({
    key: row.get('id'),
    value: row.get('id'),
    text: row.get('name'),
  }));

export const selectOptions = (reducerName, dataMapper = defaultDataMapper) =>
  createSelector(selectOrgContainerReducer(reducerName)(), dataMapper);

// Select options for our wrapped versions of Semantic UI React
const defaultDataMapperOld = (data) =>
  data.map((row) => ({
    label: row.get('name'),
    value: row.get('id'),
  }));
export const selectOptionsOld = (
  reducerName,
  dataMapper = defaultDataMapperOld,
) => createSelector(selectOrgContainerReducer(reducerName)(), dataMapper);

const usersDataMapperOld = (users) =>
  users.map((user) => {
    const userId = user.get('id');
    const fullName = user.getIn(['identity', 'fullName']);
    const email = user.getIn(['identity', 'email']);
    const idp = parseIdentityProvider(user.get('identity'));
    const status = user.get('status');

    return {
      // If the user doesn't have first and last name, show email instead
      label: fullName || email || '[Unknown Name]',
      value: userId,
      tooltipContent: (
        <TooltipContent email={email} idp={idp} status={status} />
      ),
    };
  });
export const selectUserOptionsForDropdown = selectOptionsOld(
  USERS_REDUCER,
  usersDataMapperOld,
);

export const selectRolesForMultiSelect = selectOptionsOld(ROLES_REDUCER);
export const selectReaderOptions = selectOptionsOld(READERS_REDUCER);
export const selectAlarmConfigurationOptions = selectOptionsOld(
  ALARM_CONFIGURATIONS_REDUCER,
);

export const selectAlarmActivity = selectOrgContainerReducer(
  ALARM_ACTIVITY_REDUCER,
);

// TODO - create component and fetch user avatar in it
// const usersDataMapperOld = (users) =>
//   users.map((user) => {
//     console.log({ user: user.toJS() });
//     const userId = user.get('id');
//     const fullName = user.getIn(['identity', 'fullName']);
//     const email = user.getIn(['identity', 'email']);
//     const idp = parseIdentityProvider(user.get('identity'));

//     return {
//       // If the user doesn't have first and last name, show email instead
//       label: fullName || email || '[Unknown Name]',
//       value: userId,
//       content: (
//         <Header
//           as="h4"
//           className="op-align-items-center"
//           image={{
//             avatar: true,
//             size: 'massive',
//             src: 'https://uifaces.co/our-content/donated/vIqzOHXj.jpg',
//           }}
//           content={fullName}
//           subheader={{
//             content: (
//               <div>
//                 <div>{email}</div>
//                 <div style={{ fontSize: 10 }}>{idp}</div>
//               </div>
//             ),
//           }}
//         />
//       ),
//     };
//   });

// TODO - switch out all userOptions for this one
// export const selectUserOptionsForDropdown = () => createSelector(
//   selectUsers(),
//   users => users
//     // .filter(user => user.get('status') !== 'I') // Filter out any inactive users
//     .map((user, index) => {
//       const value = user.get('id')
//       const email = user.getIn(['identity', 'email'])

//       const url = 'data:image/png;base64,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'
//       const url2 = 'https://uifaces.co/our-content/donated/vIqzOHXj.jpg'
//       return {
//         // If the user doesn't have first and last name, show email instead
//         label: generateUserLabel(user),
//         value,
//         content: (
//           <Header
//             as="h4"
//             className="op-align-items-center"
//             image={{
//               avatar: true,
//               size: 'massive',
//               src: index === 1 ? url2 : url,
//             }}
//             content={generateUserLabel(user)}
//             subheader={{
//               content: (
//                 <div>
//                   <div>{email}</div>
//                   <div style={{ fontSize: 10 }}>{parseIdentityProvider(user.get('identity'))}</div>
//                 </div>
//               ),
//             }}
//           />
//         ),
//       }
//     })
// )

// Breaks down entry unlock attempts into granted and denied per entryId
// e.g. { 123: { granted: 12, denied: 1 }}
export const selectEntryUnlockCounts = () =>
  createSelector(selectOrgContainerReducer(ACTIVITY_REDUCER)(), (activity) =>
    activity.reduce((acc, row) => {
      const entryId = row.get('entryId');
      const result = row.get('result');
      if (acc.get(entryId)) {
        return result === 'Granted'
          ? acc.setIn([entryId, 'granted'], acc.getIn([entryId, 'granted']) + 1)
          : acc.setIn([entryId, 'denied'], acc.getIn([entryId, 'denied']) + 1);
      }
      return result === 'Granted'
        ? acc.set(entryId, fromJS({ granted: 1, denied: 0 }))
        : acc.set(entryId, fromJS({ granted: 0, denied: 1 }));
    }, fromJS({})),
  );

// Groups opvideo device fragments into full-length clips
const SECONDS_BETWEEN_FRAGMENTS_THRESHOLD = 1;
export const selectOpvideoDeviceClips = createSelector(
  selectOrgContainerReducer(OPVIDEO_DEVICE_FRAGMENTS_REDUCER)(),
  (opvideoDeviceFragments) =>
    (opvideoDeviceFragments.get('fragments') || fromJS([]))
      // Sort ascending by 'producerTimestamp'
      .sort((a, b) => {
        const first = opMoment(a.get('producerTimestamp'));
        const second = opMoment(b.get('producerTimestamp'));
        if (first.isAfter(second)) {
          return 1;
        }
        if (first.isBefore(second)) {
          return -1;
        }
        return 0;
      })
      // Organize into full-length clips
      .reduce((acc, fragment) => {
        // Use valueOf so that we can maintain ms precision
        const fragmentUnixTimestamp =
          opMoment(fragment.get('producerTimestamp')).valueOf() / 1000;
        const fragmentLengthSec = fragment.get('lengthMs') / 1000;
        const lastClip = acc.get(-1);
        if (
          !lastClip ||
          lastClip.get('end') + SECONDS_BETWEEN_FRAGMENTS_THRESHOLD <
            fragmentUnixTimestamp
        ) {
          // First fragment in clip
          return acc.push(
            fromJS({
              start: fragmentUnixTimestamp,
              end: fragmentUnixTimestamp + fragmentLengthSec,
            }),
          );
        }
        return acc.setIn(
          [-1, 'end'],
          fragmentUnixTimestamp + fragmentLengthSec,
        );
      }, fromJS([])),
  // // Reverse so that newest clips are first
  // .reverse()
);

// Option creators (non-Semantic UI React)
export const selectEntryOptions = () =>
  createSelector(selectOrgContainerReducer(ENTRIES_REDUCER)(), (entries) =>
    entries.map((entry) => ({
      label: entry.get('name'),
      value: entry.get('id'),
    })),
  );
export const selectZoneOptions = () =>
  createSelector(selectOrgContainerReducer(ZONES_REDUCER)(), (zones) =>
    zones.map((zone) => ({ label: zone.get('name'), value: zone.get('id') })),
  );
export const selectSiteOptions = () =>
  createSelector(selectOrgContainerReducer(SITES_REDUCER)(), (sites) =>
    sites.map((site) => ({ label: site.get('name'), value: site.get('id') })),
  );
export const selectAllAcuOptions = () =>
  createSelector(selectOrgContainerReducer(ACUS_REDUCER)(), (acus) =>
    acus.map((acu) => ({ label: acu.get('name'), value: acu.get('id') })),
  );
export const selectAcuOptions = () =>
  createSelector(selectOrgContainerReducer(ACUS_REDUCER)(), (acus) =>
    acus
      .filter((acu) => {
        const acuModelId = acu.getIn(['acuModel', 'id']);
        return !VIDEO_READER_ACU_MODEL_IDS.includes(acuModelId);
      })
      .map((acu) => ({ label: acu.get('name'), value: acu.get('id') })),
  );

export const selectAcuGenericInputPortOptions = () =>
  createSelector(selectOrgContainerReducer(ACU_PORTS_REDUCER)(), (acuPorts) =>
    acuPorts
      .filter((acuPort) => acuPort.get('genericInput'))
      .map((acuPort) => ({
        label: `${acuPort.get('label')} (${acuPort.getIn([
          'genericInput',
          'description',
        ])})`,
        value: acuPort.get('portNumber'),
      })),
  );

export const selectVideoReaderOptions = createSelector(
  selectOrgContainerReducer(OPVIDEO_DEVICES_REDUCER)(),
  (opvideoDevices) =>
    opvideoDevices.reduce((acc, opvideoDevice) => {
      // Don't add any intercoms
      if (opvideoDevice.getIn(['opvideoIntercom', 'id'])) return acc;

      acc.push({
        label: opvideoDevice.get('name'),
        value: opvideoDevice.get('opal'),
      });

      return acc;
    }, []),
);

export const selectVideoIntercomReaderOptionsForIntercomEvents = createSelector(
  selectOrgContainerReducer(OPVIDEO_DEVICES_REDUCER)(),
  (opvideoDevices) =>
    opvideoDevices.reduce((acc, opvideoDevice) => {
      const opvideoIntercomOpal = opvideoDevice.getIn([
        'opvideoIntercom',
        'opal',
      ]);
      const opvideoIntercomName = opvideoDevice.getIn([
        'opvideoIntercom',
        'name',
      ]);

      // Don't add any non-intercoms
      if (!opvideoIntercomOpal) return acc;

      acc.push({
        label: opvideoIntercomName,
        value: opvideoIntercomOpal,
      });

      return acc;
    }, []),
);

export const selectVideoIntercomReaderOptionsForNonIntercomEvents =
  createSelector(
    selectOrgContainerReducer(OPVIDEO_DEVICES_REDUCER)(),
    (opvideoDevices) =>
      opvideoDevices.reduce((acc, opvideoDevice) => {
        // Don't add any non-intercoms
        if (!opvideoDevice.getIn(['opvideoIntercom', 'id'])) return acc;

        const opvideoIntercomName = opvideoDevice.getIn([
          'opvideoIntercom',
          'name',
        ]);
        const opvideoDeviceOpal = opvideoDevice.get('opal');

        acc.push({
          label: opvideoIntercomName,
          value: opvideoDeviceOpal,
        });

        return acc;
      }, []),
  );

export const selectAcuContactSensorPortOptions = () =>
  createSelector(selectOrgContainerReducer(ACU_PORTS_REDUCER)(), (acuPorts) =>
    acuPorts
      .filter((acuPort) => acuPort.get('contactSensor'))
      .map((acuPort) => ({
        label: acuPort.get('label'),
        value: acuPort.get('portNumber'),
      })),
  );
export const selectAcuRexPortOptions = () =>
  createSelector(selectOrgContainerReducer(ACU_PORTS_REDUCER)(), (acuPorts) =>
    acuPorts
      .filter((acuPort) => acuPort.get('rex'))
      .map((acuPort) => ({
        label: acuPort.get('label'),
        value: acuPort.get('portNumber'),
      })),
  );
export const selectAcuRelayPortOptions = () =>
  createSelector(selectOrgContainerReducer(ACU_PORTS_REDUCER)(), (acuPorts) =>
    acuPorts
      .filter((acuPort) => acuPort.get('relay'))
      .map((acuPort) => ({
        label: acuPort.get('label'),
        value: acuPort.get('portNumber'),
      })),
  );
export const selectLockdownPlanOptions = () =>
  createSelector(
    selectOrgContainerReducer(LOCKDOWN_PLANS_REDUCER)(),
    (lockdownPlans) =>
      lockdownPlans.map((l) => ({
        label: l.get('name'),
        value: l.get('id'),
      })),
  );
export const selectCredentialTypeOptions = () =>
  createSelector(
    selectOrgContainerReducer(CREDENTIAL_TYPES_REDUCER)(),
    selectFeatureFlag('ENABLED_USER_CREDENTIAL_TYPES'),
    (orgCredentialTypes, enabledCredentialTypes) =>
      orgCredentialTypes
        .filter((credentialType) =>
          enabledCredentialTypes.includes(credentialType.get('modelName')),
        )
        .map((credentialType) => ({
          label: credentialType.get('name'),
          value: credentialType.get('id'),
        })),
  );

export const selectVideoProviders = () =>
  createSelector(
    selectOrgContainerReducer(VIDEO_PROVIDERS_REDUCER)(),
    (substate) => substate,
  );

export const selectAcuEntries = (subFilter) =>
  createSelector(
    selectOrgContainerReducer(ACU_CONFIG_REDUCER, subFilter)(),
    (acuConfig) =>
      (acuConfig.get('entryId') || fromJS({})).reduce(
        (acc, value, key) => acc.push(value.set('id', +key)),
        fromJS([]),
      ),
  );

export const selectCameras = (instance) =>
  createSelector(selectPage(), (page) =>
    page.getIn([instance || 'cameras'], fromJS([])),
  );

export const selectCamerasForMultiSelect = () =>
  createSelector(selectCameras(), (orgCameras) =>
    orgCameras.map((camera) => ({
      label: camera.get('nameExt'),
      value: camera.get('id'),
    })),
  );
